/*
 * PostHog Helper Utilities
 * Common utilities for consistent PostHog event tracking
 */

/**
 * Get common page context information
 */
export const getPageContext = () => {
    if (typeof window === 'undefined') return {};

    return {
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        page_referrer: document.referrer || undefined,
    };
};

/**
 * Get UTM parameters from URL
 */
export const getUTMParams = () => {
    if (typeof window === 'undefined') return {};

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach(key => {
        const value = urlParams.get(key);
        if (value) {
            utmParams[key] = value;
        }
    });

    return utmParams;
};

/**
 * Extract element information for click tracking
 */
export const getElementInfo = (element: HTMLElement) => {
    const info: Record<string, any> = {
        element_type: element.tagName.toLowerCase(),
    };

    // Get element text (truncated to 100 chars)
    const text = element.textContent?.trim() || element.getAttribute('aria-label');
    if (text) {
        info.element_text = text.substring(0, 100);
    }

    // Get element ID
    if (element.id) {
        info.element_id = element.id;
    }

    // Get classes (first 3)
    if (element.className && typeof element.className === 'string') {
        const classes = element.className.split(' ').filter(c => c).slice(0, 3);
        if (classes.length > 0) {
            info.element_classes = classes.join(' ');
        }
    }

    // For links
    if (element.tagName === 'A') {
        const href = element.getAttribute('href');
        if (href) {
            info.link_url = href;
            try {
                const url = new URL(href, window.location.href);
                info.link_domain = url.hostname;
            } catch (e) {
                // Relative URL or invalid
            }
        }
    }

    // Get section context (look for closest section, article, or data-section)
    const section = element.closest('[data-section]') || element.closest('section') || element.closest('article');
    if (section) {
        info.section = (section as HTMLElement).dataset.section ||
                      section.id ||
                      section.className.split(' ')[0];
    }

    return info;
};

/**
 * Check if a link is outbound
 */
export const isOutboundLink = (url: string): boolean => {
    if (typeof window === 'undefined') return false;

    try {
        const linkUrl = new URL(url, window.location.href);
        return linkUrl.hostname !== window.location.hostname;
    } catch (e) {
        return false;
    }
};

/**
 * Check if a link is a download
 */
export const isDownloadLink = (url: string): boolean => {
    const downloadExtensions = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
        'zip', 'rar', '7z', 'tar', 'gz',
        'mp3', 'mp4', 'avi', 'mov', 'wmv',
        'jpg', 'jpeg', 'png', 'gif', 'svg',
        'csv', 'txt', 'json', 'xml'
    ];

    try {
        const linkUrl = new URL(url, window.location.href);
        const extension = linkUrl.pathname.split('.').pop()?.toLowerCase();
        return extension ? downloadExtensions.includes(extension) : false;
    } catch (e) {
        return false;
    }
};

/**
 * Get file extension from URL
 */
export const getFileExtension = (url: string): string | undefined => {
    try {
        const linkUrl = new URL(url, window.location.href);
        const extension = linkUrl.pathname.split('.').pop()?.toLowerCase();
        return extension;
    } catch (e) {
        return undefined;
    }
};

/**
 * Get file name from URL
 */
export const getFileName = (url: string): string | undefined => {
    try {
        const linkUrl = new URL(url, window.location.href);
        const pathParts = linkUrl.pathname.split('/');
        return pathParts[pathParts.length - 1];
    } catch (e) {
        return undefined;
    }
};

/**
 * Debounce function for performance-sensitive tracking
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Throttle function for high-frequency events
 */
export const throttle = <T extends (...args: any[]) => any>(
    func: T,
    limit: number
): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean = false;

    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

/**
 * Calculate scroll depth percentage
 */
export const getScrollDepth = (): number => {
    if (typeof window === 'undefined') return 0;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
    );
    const windowHeight = window.innerHeight;
    const scrollPercent = (scrollTop / (docHeight - windowHeight)) * 100;

    return Math.min(Math.round(scrollPercent), 100);
};

/**
 * Get session storage with fallback
 */
export const getSessionData = (key: string): any => {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;

    try {
        const data = sessionStorage.getItem(key);
        return data ? JSON.parse(data) : null;
    } catch (e) {
        console.error('[PostHog] Error reading session storage:', e);
        return null;
    }
};

/**
 * Set session storage with fallback
 */
export const setSessionData = (key: string, value: any): void => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;

    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.error('[PostHog] Error writing session storage:', e);
    }
};

/**
 * Generate a simple unique ID for session tracking
 */
export const generateSessionId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create session ID
 */
export const getOrCreateSessionId = (): string => {
    const SESSION_KEY = 'posthog_session_id';
    let sessionId = getSessionData(SESSION_KEY);

    if (!sessionId) {
        sessionId = generateSessionId();
        setSessionData(SESSION_KEY, sessionId);
    }

    return sessionId;
};

/**
 * Get environment info for super properties
 */
export const getEnvironmentInfo = () => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Use Vercel's environment variable for accurate detection
    const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;

    // Production is ONLY www.muzology.com OR NEXT_PUBLIC_VERCEL_ENV === 'production'
    const isProduction = typeof window !== 'undefined'
        ? window.location.hostname === 'www.muzology.com'
        : vercelEnv === 'production';

    // Preview includes Vercel preview deployments and known staging URLs
    const isPreview = vercelEnv === 'preview' ||
        (typeof window !== 'undefined' &&
            (window.location.hostname.includes('next.muztech.net') ||
             window.location.hostname.includes('web-staging.muzology.com') ||
             window.location.hostname.includes('.vercel.app')));

    return {
        environment: isProduction ? 'production' : (isPreview ? 'preview' : 'development'),
        is_production: isProduction,
        is_preview: isPreview,
        is_development: isDevelopment,
        build_version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'local',
        build_branch: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF || 'unknown',
    };
};

/**
 * Get device info
 */
export const getDeviceInfo = () => {
    if (typeof window === 'undefined') return {};

    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
    const isDesktop = !isMobile && !isTablet;

    return {
        device_type: isMobile ? 'mobile' : (isTablet ? 'tablet' : 'desktop'),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
    };
};
